import { Injectable, inject } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { dateFormatEnum } from './date-constants';
import { DateService } from './date.service';

@Injectable({
  providedIn: 'root'
})
export class DateControlValidationsService {
  private readonly dateService = inject(DateService);

  isNonSchoolDate(dt: Date, nonSchoolDates): boolean {
    return this.dateService.IsDateExistInDatesCollection(dt, nonSchoolDates);
  }

  // Check whether the date is non school date
  checkNonSchoolDate(
    dt: Date,
    nonSchoolDates,
    dateField: AbstractControl
  ): boolean {
    dateField.setErrors(null);
    if (this.dateService.IsDateExistInDatesCollection(dt, nonSchoolDates)) {
      dateField.setErrors({ nonSchoolDateEntered: true });
      return false;
    }
    return true;
  }

  // Check whether the date is after academic year end date
  checkDateAfterAcademicYearEndDate(
    dt: Date,
    maxAcademicYearDate: Date,
    dateField: AbstractControl
  ): boolean {
    dateField.setErrors(null);
    if (
      dt.getTime() >
      new Date(maxAcademicYearDate.setHours(23, 59, 59)).getTime()
    ) {
      dateField.setErrors({ dateAfterAcademicYearEndDate: true });
      return false;
    }
    return true;
  }

  // Check whether the date is after exclusion end date
  checkDateAfterExclusionEndDate(
    dt: Date,
    exclusionEndDate: Date,
    dateField: AbstractControl
  ): boolean {
    dateField.setErrors(null);
    if (
      exclusionEndDate &&
      dt.getTime() > new Date(exclusionEndDate).getTime()
    ) {
      dateField.setErrors({ dateAfterExclEndDate: true });
      return false;
    }
    return true;
  }

  //Check whether the Start Date is after exclusion end date
  checkStartDateAfterExclusionEndDate(
    dt: Date,
    exclusionEndDate: Date,
    dateField: AbstractControl
  ): boolean {
    dateField.setErrors(null);
    if (
      exclusionEndDate &&
      dt.getTime() > new Date(exclusionEndDate).getTime()
    ) {
      dateField.setErrors({ isInvalidStartDate: true });
      return false;
    }
    return true;
  }

  //Check whether the Start Date is after date off roll
  checkStartDateAfterDateOffRoll(
    dt: Date,
    dateOffRoll: Date,
    dateField: AbstractControl
  ): boolean {
    dateField.setErrors(null);
    if (dateOffRoll && dt.getTime() > new Date(dateOffRoll).getTime()) {
      dateField.setErrors({ isStartDateAfterDateOffRoll: true });
      return false;
    }
    return true;
  }

  // Check whether the date passed is before the meeting requested date
  checkDateBeforeMeetingRequestedDate(
    dt: Date,
    dateToCompare: Date,
    dateField: AbstractControl
  ) {
    dateField.setErrors(null);
    if (dt.getTime() < new Date(dateToCompare).getTime()) {
      dateField.setErrors({ dateBeforeMeetingRequestedDate: true });
      return false;
    }
    return true;
  }

  // Check whether the date off roll is before the exclusion start date
  checkDateOffRollBeforeExclusionStartDate(
    dt: Date,
    exclusionStartDate: Date,
    dateField: AbstractControl
  ): boolean {
    dateField.setErrors(null);
    if (dt.getTime() < new Date(exclusionStartDate).getTime()) {
      dateField.setErrors({ isDateOffRollBeforeStartDate: true });
      return false;
    }

    return true;
  }

  // Check whether the date is before the exclusion start date
  checkDateBeforeExclusionStartDate(
    dt: Date,
    exclusionStartDate: Date,
    dateField: AbstractControl
  ): boolean {
    dateField.setErrors(null);
    if (dt.getTime() < new Date(exclusionStartDate).getTime()) {
      dateField.setErrors({ dateBeforeExclStartDate: true });
      return false;
    }
    return true;
  }

  // Check the date format entered is valid
  checkDateFormat(
    inputDate: string,
    format: dateFormatEnum,
    dateField: AbstractControl
  ): boolean {
    const isValidDate = this.dateService.IsValidDateFormat(inputDate, format);
    if (!isValidDate) {
      dateField.setErrors({
        invalidDateFormat: true
      });
      return false;
    }
    const date = this.dateService.GetDateFromString(inputDate, format);
    if (date.toString() == 'Invalid Date') {
      dateField.setErrors({
        invalidDateFormat: true
      });
      return false;
    }
    return true;
  }

  // Check whether the date entered is exceeding the max date limit set on the calender
  checkDateAfterMaxDateLimit(
    dt: Date,
    maxDateLimit: Date,
    dateField: AbstractControl
  ) {
    dateField.setErrors(null);
    if (dt.getTime() > new Date(maxDateLimit.toDateString()).getTime()) {
      dateField.setErrors({ nonSchoolDateEntered: true });
      return false;
    }
    return true;
  }

  // Check whether the date entered is exceeding the min date limit set on the calender
  checkDateBeforeMinDateLimit(
    dt: Date,
    minDateLimit: Date,
    dateField: AbstractControl
  ) {
    dateField.setErrors(null);
    if (dt.getTime() < new Date(minDateLimit.toDateString()).getTime()) {
      dateField.setErrors({ nonSchoolDateEntered: true });
      return false;
    }
    return true;
  }
}
